import { useState } from 'react';
import { apiGet, apiPatch, apiDelete, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { UserRole } from '../lib/roles';
import { useModal } from '../contexts/ModalContext';
import { DataTable } from '../components/DataTable';
import { Checkbox } from '../components/FormCheck';

interface ManagedUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  specialty?: string;
  verificationStatus?: string;
  /** Per-user permission overrides — additive on top of the role default (ADM-23). */
  permissions?: string[];
}

interface PermissionMeta {
  permission: string;
  module: string;
  scope: 'view' | 'action';
}

interface PaginatedUsers {
  items: ManagedUser[];
  total: number;
  page: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

const ROLE_OPTIONS = Object.values(UserRole);

export function UserManagementPage() {
  const { promptConfirm } = useModal();
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [lookupId, setLookupId] = useState('');
  const [lookedUpUser, setLookedUpUser] = useState<ManagedUser | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // ADM-23 — expandable "Manage Access" panel, replacing the old prompt()-based role change.
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [pendingRole, setPendingRole] = useState<UserRole | ''>('');
  const [pendingPermissions, setPendingPermissions] = useState<string[]>([]);

  const { data, loading, error, reload } = useApiResource(
    () =>
      apiGet<PaginatedUsers>(
        `/users?page=${page}&limit=20${roleFilter ? `&role=${roleFilter}` : ''}`,
      ),
    [page, roleFilter],
  );

  const { data: catalog } = useApiResource(() => apiGet<PermissionMeta[]>('/permissions'), []);

  const runLookup = async () => {
    if (!lookupId.trim()) return;
    setLookupError(null);
    setLookedUpUser(null);
    try {
      const user = await apiGet<ManagedUser>(`/users/${encodeURIComponent(lookupId.trim())}`);
      setLookedUpUser(user);
    } catch (err) {
      setLookupError(err instanceof ApiError ? err.message : 'User not found');
    }
  };

  const openPanel = (user: ManagedUser) => {
    if (expandedUserId === user.id) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(user.id);
    setPendingRole(user.role);
    setPendingPermissions(user.permissions ?? []);
    setActionError(null);
  };

  const saveRole = async (user: ManagedUser) => {
    if (!pendingRole || pendingRole === user.role) return;
    setBusyId(user.id);
    setActionError(null);
    try {
      await apiPatch(`/users/${user.id}/role`, { role: pendingRole });
      reload();
      if (lookedUpUser?.id === user.id) void runLookup();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Role update failed');
    } finally {
      setBusyId(null);
    }
  };

  const togglePendingPermission = (permission: string, checked: boolean) => {
    setPendingPermissions((prev) =>
      checked ? [...prev, permission] : prev.filter((p) => p !== permission),
    );
  };

  const savePermissions = async (user: ManagedUser) => {
    setBusyId(user.id);
    setActionError(null);
    try {
      await apiPatch(`/users/${user.id}/permissions`, { permissions: pendingPermissions });
      reload();
      if (lookedUpUser?.id === user.id) void runLookup();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Permission update failed');
    } finally {
      setBusyId(null);
    }
  };

  const verifySpecialist = async (user: ManagedUser, isVerified: boolean) => {
    setBusyId(user.id);
    setActionError(null);
    try {
      await apiPatch(`/users/admin/${user.id}/verify`, { isVerified });
      reload();
      if (lookedUpUser?.id === user.id) void runLookup();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Verification update failed');
    } finally {
      setBusyId(null);
    }
  };

  const deleteUser = async (user: ManagedUser) => {
    const ok = await promptConfirm({
      title: 'Delete user',
      message: `Type the user's email to permanently delete this account. This cannot be undone.`,
      matchText: user.email,
      confirmLabel: 'Delete account',
      danger: true,
    });
    if (!ok) return;
    setBusyId(user.id);
    setActionError(null);
    try {
      await apiDelete(`/users/${user.id}`);
      reload();
      if (lookedUpUser?.id === user.id) setLookedUpUser(null);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const renderActions = (user: ManagedUser) => (
    <div className="row-actions">
      <button className="btn" disabled={busyId === user.id} onClick={() => openPanel(user)}>
        {expandedUserId === user.id ? 'Close' : 'Manage access'}
      </button>
      {user.role === UserRole.SPECIALIST && (
        <>
          <button className="btn btn-primary" disabled={busyId === user.id} onClick={() => verifySpecialist(user, true)}>
            Verify
          </button>
          <button className="btn" disabled={busyId === user.id} onClick={() => verifySpecialist(user, false)}>
            Unverify
          </button>
        </>
      )}
      <button className="btn btn-danger" disabled={busyId === user.id} onClick={() => deleteUser(user)}>
        Delete
      </button>
    </div>
  );

  /** Contents of the expanded drawer; DataTable supplies the full-width row around it. */
  const renderAccessPanel = (user: ManagedUser) => {
    if (expandedUserId !== user.id) return null;
    return (
          <div className="form-card" style={{ maxWidth: 'none' }}>
            <label htmlFor={`role-${user.id}`}>Role</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select
                id={`role-${user.id}`}
                value={pendingRole}
                onChange={(e) => setPendingRole(e.target.value as UserRole)}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busyId === user.id || pendingRole === user.role}
                onClick={() => void saveRole(user)}
              >
                Save role
              </button>
            </div>

            <label style={{ marginTop: '0.75rem' }}>
              Permission overrides — additive on top of the {pendingRole || user.role} role default
            </label>
            {!catalog ? (
              <p className="form-hint">Loading permission catalog…</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1.2rem' }}>
                {catalog.map((perm) => (
                  <Checkbox
                    key={perm.permission}
                    checked={pendingPermissions.includes(perm.permission)}
                    label={perm.permission}
                    onChange={(next) => togglePendingPermission(perm.permission, next)}
                  />
                ))}
              </div>
            )}
            <div>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busyId === user.id}
                onClick={() => void savePermissions(user)}
              >
                Save permission overrides
              </button>
            </div>
          </div>
    );
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>User Management</h1>
          <p>Filter and page through users, or look up one directly by ID (there's no free-text search endpoint yet).</p>
        </div>
      </div>

      <div className="form-card" style={{ marginBottom: '1.5rem' }}>
        <label htmlFor="lookupId">Look up a user by ID</label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            id="lookupId"
            value={lookupId}
            onChange={(e) => setLookupId(e.target.value)}
            placeholder="Firebase UID"
            style={{ flex: 1 }}
          />
          <button type="button" className="btn btn-primary" onClick={() => void runLookup()}>
            Look up
          </button>
        </div>
        {lookupError && <p className="auth-error">{lookupError}</p>}
        {lookedUpUser && (
          <div style={{ marginTop: '0.5rem' }}>
            {/* One row by definition — no pager, no search. */}
            <DataTable
              rows={[lookedUpUser]}
              getRowKey={(u) => u.id}
              pageSize={null}
              expandedRow={renderAccessPanel}
              columns={[
                { key: 'email', header: 'Email', value: (u) => u.email },
                { key: 'name', header: 'Name', value: (u) => u.name },
                { key: 'role', header: 'Role', value: (u) => u.role },
                { key: 'actions', header: 'Actions', render: renderActions },
              ]}
            />
          </div>
        )}
      </div>

      <div className="section-toolbar">
        <label htmlFor="role-filter">Role</label>
        <select
          id="role-filter"
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All</option>
          {ROLE_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <button className="btn" onClick={reload}>
          Refresh
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {actionError && <div className="error-banner">{actionError}</div>}

      <DataTable
        id="user-management"
        rows={data?.items}
        loading={loading}
        empty="No users match this filter."
        noun="users"
        getRowKey={(user) => user.id}
        expandedRow={renderAccessPanel}
        pagination={
          data
            ? {
                page: data.page,
                totalPages: data.totalPages,
                total: data.total,
                hasNext: data.hasNext,
                hasPrev: data.hasPrev,
                onPageChange: setPage,
                noun: 'users',
              }
            : undefined
        }
        columns={[
          { key: 'email', header: 'Email', value: (u) => u.email },
          { key: 'name', header: 'Name', value: (u) => u.name || '—' },
          { key: 'role', header: 'Role', value: (u) => u.role, filter: 'select' },
          { key: 'specialty', header: 'Specialty', value: (u) => u.specialty || '—', filter: 'select' },
          { key: 'actions', header: 'Actions', render: renderActions },
        ]}
      />
    </div>
  );
}
